(() => {
  if (window.VarietySectionSticky) return;

  const defaultHeaderSelectors = [
    'header.section-header',
    '#shopify-section-header',
    '.shopify-section-header-sticky',
    '.header-wrapper',
    'header.header'
  ];

  function parsePx(value) {
    if (!value) return 0;
    const parsed = parseFloat(String(value).trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function getStickyHeaderBottomFromThemeState() {
    if (!document.body) return null;

    const body = document.body;
    const bodyStyle = window.getComputedStyle(body);
    const dynamicHeaderHeight = parsePx(bodyStyle.getPropertyValue('--dynamic_header_height'));
    const baseHeaderHeight = parsePx(bodyStyle.getPropertyValue('--header_height'));
    const isHeaderSticky = body.classList.contains('header-sticky');

    // header.js hides sticky header by setting dynamic height to 0 and removing header-sticky.
    if (!isHeaderSticky && dynamicHeaderHeight <= 0) return 0;

    if (dynamicHeaderHeight > 0) return dynamicHeaderHeight;
    if (baseHeaderHeight > 0) return baseHeaderHeight;

    return null;
  }

  function getStickyHeaderBottom(selectors = defaultHeaderSelectors, useThemeHeaderState = true) {
    if (useThemeHeaderState) {
      const stateValue = getStickyHeaderBottomFromThemeState();
      if (stateValue !== null) return Math.max(0, stateValue);
    }

    let bottom = 0;
    selectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom <= 0) return;

        const computedStyle = window.getComputedStyle(el);
        const isStickyLike =
          computedStyle.position === 'fixed' ||
          computedStyle.position === 'sticky' ||
          el.classList.contains('is-sticky') ||
          el.classList.contains('shopify-section-header-sticky');

        if (!isStickyLike) return;
        bottom = Math.max(bottom, rect.bottom);
      });
    });

    return Math.max(0, bottom);
  }

  function initBottomBoundarySticky(options) {
    const {
      section,
      target,
      spacer,
      fixedClass,
      offsetVar,
      thresholdBuffer = 2,
      headerSelectors = defaultHeaderSelectors,
      useThemeHeaderState = true,
      lockInlineSize = false,
      onPin,
      onUnpin
    } = options;

    if (!section || !target || !spacer || !fixedClass || !offsetVar) return () => {};

    let triggerY = 0;

    const isPinned = () => target.classList.contains(fixedClass);

    const clearInlineSize = () => {
      target.style.left = '';
      target.style.width = '';
    };

    const setInlineSize = () => {
      if (!lockInlineSize) return;
      const rect = target.getBoundingClientRect();
      target.style.left = `${Math.round(rect.left)}px`;
      target.style.width = `${Math.round(rect.width)}px`;
      spacer.style.height = `${Math.round(rect.height)}px`;
    };

    const pin = (headerBottom) => {
      setInlineSize();
      if (!lockInlineSize) {
        spacer.style.height = `${Math.round(target.getBoundingClientRect().height)}px`;
      }
      section.style.setProperty(offsetVar, `${headerBottom}px`);
      target.classList.add(fixedClass);
      if (typeof onPin === 'function') onPin();
    };

    const unpin = () => {
      target.classList.remove(fixedClass);
      clearInlineSize();
      spacer.style.height = '0px';
      if (typeof onUnpin === 'function') onUnpin();
    };

    const measureTrigger = () => {
      const pinned = isPinned();
      if (pinned) {
        target.classList.remove(fixedClass);
        clearInlineSize();
        spacer.style.height = '0px';
      }

      const rect = section.getBoundingClientRect();
      triggerY = rect.bottom + window.scrollY;

      if (pinned) {
        pin(getStickyHeaderBottom(headerSelectors, useThemeHeaderState));
      }
    };

    const onScroll = () => {
      const headerBottom = getStickyHeaderBottom(headerSelectors, useThemeHeaderState);
      const currentPinned = isPinned();
      const scrollAnchor = window.scrollY + headerBottom;

      if (!currentPinned && scrollAnchor >= triggerY + thresholdBuffer) {
        pin(headerBottom);
      } else if (currentPinned && scrollAnchor <= triggerY - thresholdBuffer) {
        unpin();
      } else if (currentPinned) {
        section.style.setProperty(offsetVar, `${headerBottom}px`);
      }
    };

    const onResize = () => {
      measureTrigger();
      onScroll();
    };

    measureTrigger();
    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('shopify:section:reorder', onResize);
    document.addEventListener('shopify:section:load', onResize);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('shopify:section:reorder', onResize);
      document.removeEventListener('shopify:section:load', onResize);
    };
  }

  function initSentinelSticky(options) {
    const {
      target,
      spacer,
      sentinel,
      fixedClass,
      offsetVar,
      headerSelectors = defaultHeaderSelectors,
      useThemeHeaderState = true
    } = options;

    if (!target || !spacer || !sentinel || !fixedClass || !offsetVar) return () => {};

    const getHeaderHeight = () => getStickyHeaderBottom(headerSelectors, useThemeHeaderState);

    let observer = null;
    let rafId = null;
    let lastSpacerHeight = 0;
    let lastOffset = 0;

    const updateLayout = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;

        const headerH = getHeaderHeight();
        const fixedH = target.getBoundingClientRect().height;

        if (Math.abs(lastOffset - headerH) > 0.5) {
          target.style.setProperty(offsetVar, `${headerH}px`);
          lastOffset = headerH;
          rebuildObserver(headerH);
        }

        const targetHeight = target.classList.contains(fixedClass) ? fixedH : 0;
        if (Math.abs(lastSpacerHeight - targetHeight) > 0.5) {
          spacer.style.height = `${targetHeight}px`;
          lastSpacerHeight = targetHeight;
        }
      });
    };

    const rebuildObserver = (headerH) => {
      if (observer) observer.disconnect();

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              target.classList.remove(fixedClass);
            } else if (entry.boundingClientRect.top < headerH) {
              target.classList.add(fixedClass);
            }
            updateLayout();
          });
        },
        { threshold: 0, rootMargin: `-${headerH}px 0px 0px 0px` }
      );

      observer.observe(sentinel);
    };

    const initialHeaderH = getHeaderHeight();
    target.style.setProperty(offsetVar, `${initialHeaderH}px`);
    lastOffset = initialHeaderH;
    rebuildObserver(initialHeaderH);

    window.addEventListener('resize', updateLayout);

    let headerResizeObserver;
    let targetResizeObserver;

    if ('ResizeObserver' in window) {
      headerResizeObserver = new ResizeObserver(updateLayout);
      headerSelectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((node) => headerResizeObserver.observe(node));
      });

      targetResizeObserver = new ResizeObserver(updateLayout);
      targetResizeObserver.observe(target);
    }

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', updateLayout);
      if (headerResizeObserver) headerResizeObserver.disconnect();
      if (targetResizeObserver) targetResizeObserver.disconnect();
    };
  }

  window.VarietySectionSticky = {
    getStickyHeaderBottom,
    initBottomBoundarySticky,
    initSentinelSticky
  };
})();
